import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import type { CrmDataProvider } from "../providers/types";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { EmailRecipient, RAFile } from "../types";
import { ComposeAttachments } from "./ComposeAttachments";
import { fillTemplate, type TemplateVariables } from "./emailTemplates";
import type { EmailLinks } from "./mailTypes";
import { RecipientsInput } from "./RecipientsInput";
import { mailErrorKey, useMailAccount } from "./useMailAccount";

/** What the compose window opens with. */
export interface EmailDraft {
  to?: EmailRecipient[];
  cc?: EmailRecipient[];
  subject?: string;
  body?: string;
  links: EmailLinks;
  in_reply_to?: string;
  references?: string[];
  /** Files already joined, like those of a forwarded message. */
  attachments?: RAFile[];
}

export interface ComposeEmailDialogProps {
  open: boolean;
  onClose: () => void;
  draft: EmailDraft;
  /** Known addresses of the record, offered in one click. */
  suggestions?: EmailRecipient[];
  /** Values of the template placeholders (contact, company). */
  variables?: TemplateVariables;
  /** Company whose quotes can be attached. */
  companyId?: Identifier;
}

/** Writes and sends an email from the user's own mailbox. */
export const ComposeEmailDialog = (props: ComposeEmailDialogProps) => {
  const translate = useTranslate();
  return (
    <Dialog open={props.open} onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-9/10 overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{translate("crm.mail.compose.title")}</DialogTitle>
        </DialogHeader>
        {/* Mounted with the dialog, so each opening starts from the draft. */}
        {props.open ? <ComposeForm {...props} /> : null}
      </DialogContent>
    </Dialog>
  );
};

const ComposeForm = ({
  onClose,
  draft,
  suggestions,
  variables,
  companyId,
}: ComposeEmailDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { identity } = useGetIdentity();
  const { emailTemplates } = useConfigurationContext();
  const { data: account, isPending } = useMailAccount();
  const [to, setTo] = useState<EmailRecipient[]>(draft.to ?? []);
  const [cc, setCc] = useState<EmailRecipient[]>(draft.cc ?? []);
  const [bcc, setBcc] = useState<EmailRecipient[]>([]);
  const [hasCopies, setHasCopies] = useState(!!draft.cc?.length);
  const [subject, setSubject] = useState(draft.subject ?? "");
  const [body, setBody] = useState(draft.body ?? "");
  const [attachments, setAttachments] = useState<RAFile[]>(
    draft.attachments ?? [],
  );

  const [firstName, ...lastNames] = (identity?.fullName ?? "").split(" ");
  const templateVariables: TemplateVariables = {
    ...variables,
    mon_prenom: firstName,
    mon_nom: lastNames.join(" "),
  };

  const send = useMutation({
    mutationFn: () =>
      dataProvider.sendEmail({
        to,
        cc,
        bcc,
        subject: subject.trim(),
        body_text: body,
        attachments,
        links: draft.links,
        in_reply_to: draft.in_reply_to,
        references: draft.references,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emails"] });
      queryClient.invalidateQueries({ queryKey: ["activity_log"] });
      queryClient.invalidateQueries({ queryKey: ["mailbox"] });
      notify("crm.mail.compose.sent", { type: "success" });
      onClose();
    },
    onError: (error) => notify(mailErrorKey(error), { type: "error" }),
  });

  if (isPending) return null;
  if (account?.mail_status !== "connected") {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm">{translate("crm.mail.compose.not_connected")}</p>
        <DialogFooter>
          <Button asChild>
            <Link to="/mail-settings" onClick={onClose}>
              {translate("crm.mail.compose.connect")}
            </Link>
          </Button>
        </DialogFooter>
      </div>
    );
  }

  const applyTemplate = (index: string) => {
    const template = emailTemplates[Number(index)];
    if (!template) return;
    setSubject(fillTemplate(template.subject, templateVariables));
    setBody(fillTemplate(template.body, templateVariables));
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!to.length) {
      notify("crm.mail.errors.missing_recipient", { type: "error" });
      return;
    }
    send.mutate();
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
      <p className="text-xs text-muted-foreground">
        {translate("crm.mail.compose.from", { email: account.email })}
      </p>
      <RecipientsInput
        id="compose-to"
        label={translate("crm.mail.compose.to")}
        value={to}
        onChange={setTo}
        suggestions={suggestions}
      />
      {hasCopies ? (
        <>
          <RecipientsInput
            id="compose-cc"
            label={translate("crm.mail.compose.cc")}
            value={cc}
            onChange={setCc}
          />
          <RecipientsInput
            id="compose-bcc"
            label={translate("crm.mail.compose.bcc")}
            value={bcc}
            onChange={setBcc}
          />
        </>
      ) : (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="self-start px-0"
          onClick={() => setHasCopies(true)}
        >
          {translate("crm.mail.compose.add_copies")}
        </Button>
      )}
      {emailTemplates.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="compose-template">
            {translate("crm.mail.compose.template")}
          </Label>
          <Select onValueChange={applyTemplate}>
            <SelectTrigger id="compose-template" className="w-full">
              <SelectValue
                placeholder={translate("crm.mail.compose.template_placeholder")}
              />
            </SelectTrigger>
            <SelectContent>
              {emailTemplates.map((template, index) => (
                <SelectItem key={index} value={String(index)}>
                  {template.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="compose-subject">
          {translate("crm.mail.compose.subject")}
        </Label>
        <Input
          id="compose-subject"
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          required
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="compose-body">
          {translate("crm.mail.compose.body")}
        </Label>
        <Textarea
          id="compose-body"
          rows={10}
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />
        {account.signature_text || account.signature_image ? (
          <div className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground whitespace-pre-line">
            {account.signature_text}
            {account.signature_image ? (
              <img
                src={account.signature_image.src}
                alt=""
                className="mt-1 max-h-10"
              />
            ) : null}
          </div>
        ) : null}
      </div>
      <ComposeAttachments
        value={attachments}
        onChange={setAttachments}
        onTooLarge={() =>
          notify("crm.mail.errors.attachments_too_large", { type: "error" })
        }
        companyId={companyId}
      />
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {translate("ra.action.cancel")}
        </Button>
        <Button type="submit" disabled={send.isPending}>
          {translate(
            send.isPending
              ? "crm.mail.compose.sending"
              : "crm.mail.compose.send",
          )}
        </Button>
      </DialogFooter>
    </form>
  );
};
